import React, { useState, useCallback, createContext, useContext } from 'react';

// Context per gestire il dialog globalmente
const ConfirmDialogContext = createContext(null);

// Provider component
export const ConfirmDialogProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Conferma',
    cancelText: 'Annulla',
    variant: 'danger', // 'danger', 'warning', 'info'
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, confirmText, cancelText, variant, onConfirm }) => {
    setDialogState({
      isOpen: true,
      title: title || 'Conferma',
      message: message || 'Sei sicuro di voler procedere?',
      confirmText: confirmText || 'Conferma',
      cancelText: cancelText || 'Annulla',
      variant: variant || 'danger',
      onConfirm,
    });
  }, []);

  const hideConfirm = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (dialogState.onConfirm) {
      dialogState.onConfirm();
    }
    hideConfirm();
  }, [dialogState.onConfirm, hideConfirm]);

  return (
    <ConfirmDialogContext.Provider value={{ showConfirm, hideConfirm }}>
      {children}
      {dialogState.isOpen && (
        <ConfirmDialogModal
          {...dialogState}
          onConfirm={handleConfirm}
          onCancel={hideConfirm}
        />
      )}
    </ConfirmDialogContext.Provider>
  );
};

// Hook per usare il dialog
export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};

// Componente Modal
const ConfirmDialogModal = ({ 
  isOpen, 
  title, 
  message, 
  confirmText, 
  cancelText, 
  variant,
  onConfirm, 
  onCancel 
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: '🗑️',
      confirmBg: 'bg-red-600 hover:bg-red-700',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600'
    },
    warning: {
      icon: '⚠️',
      confirmBg: 'bg-orange-600 hover:bg-orange-700',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    info: {
      icon: 'ℹ️',
      confirmBg: 'bg-blue-600 hover:bg-blue-700',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    }
  };

  const style = variantStyles[variant] || variantStyles.danger;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto" data-testid="confirm-dialog">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity z-[10000]"
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className="fixed inset-0 flex min-h-full items-center justify-center p-4 z-[10001]">
        <div 
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in-95 duration-200"
          onClick={e => e.stopPropagation()}
        >
          {/* Content */}
          <div className="p-6">
            {/* Icon */}
            <div className={`mx-auto w-14 h-14 ${style.iconBg} rounded-full flex items-center justify-center mb-4`}>
              <span className="text-2xl">{style.icon}</span>
            </div>
            
            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
              {title}
            </h3>
            
            {/* Message */}
            <p className="text-gray-600 text-center mb-6">
              {message}
            </p>
            
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                data-testid="confirm-dialog-cancel"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                className={`flex-1 px-4 py-3 ${style.confirmBg} text-white font-medium rounded-xl transition-colors`}
                data-testid="confirm-dialog-confirm"
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente standalone per uso senza context
export const ConfirmDialog = ConfirmDialogModal;

export default ConfirmDialogModal;
